import { useAuth } from "@/context/auth-provider";
import {
  CategoryLimitAPI,
  extractLimitErrorMessage,
  normalizeCategoryLimitSummary,
} from "@/services/category-limit.service";
import {
  CategoryLimitProgress,
  CategoryLimitSummary,
} from "@/types/category-limit.type";
import React from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "./use-toast";

/** Root key — every month's query hangs off this, so invalidating it refetches all. */
export const CATEGORY_LIMITS_QUERY_KEY = ["category-limits"];

/** Cache key for a given month; the current month (no arg) caches as "current". */
export const categoryLimitsQueryKey = (month?: string) => [
  "category-limits",
  month ?? "current",
];

const EMPTY_SUMMARY: CategoryLimitSummary = {
  month: "",
  periodStart: "",
  periodEnd: "",
  count: 0,
  totals: { limit: 0, spent: 0, remaining: 0 },
  results: [],
};

export interface UseCategoryLimitsParams {
  enabled?: boolean;
  /**
   * "YYYY-MM" month to load. Callers pass this explicitly (including for the
   * current month) so the client and the backend never disagree about which
   * month is shown — an omitted month resolves in UTC on the backend, which can
   * differ from the browser near a month boundary. Each month caches
   * independently, so two surfaces sharing a month share its cache entry.
   */
  month?: string;
}

/**
 * Current-month category limits ("budgets-lite").
 *
 * Available to every authenticated user — limits are deliberately NOT a
 * premium feature (creating custom *categories* is, and that is gated
 * elsewhere).
 */
const useCategoryLimits = (params: UseCategoryLimitsParams = {}) => {
  const { enabled = true, month } = params;
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const isAuthenticated = !!user;

  const fetchCurrentMonth = async (): Promise<CategoryLimitSummary> => {
    if (!isAuthenticated) return EMPTY_SUMMARY;
    const response = await CategoryLimitAPI.getCurrentMonth(month);
    return normalizeCategoryLimitSummary(response?.data);
  };

  const {
    data: summary,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<CategoryLimitSummary>(
    categoryLimitsQueryKey(month),
    fetchCurrentMonth,
    {
      refetchOnWindowFocus: false,
      enabled: isAuthenticated && enabled,
      keepPreviousData: true,
    }
  );

  /** Limit progress by category id, for joining against a category list. */
  const limitsByCategory = React.useMemo(() => {
    const map = new Map<number, CategoryLimitProgress>();
    for (const result of summary?.results ?? []) {
      map.set(result.category, result);
    }
    return map;
  }, [summary]);

  const getLimitForCategory = React.useCallback(
    (categoryId?: number) =>
      typeof categoryId === "number"
        ? limitsByCategory.get(categoryId)
        : undefined,
    [limitsByCategory]
  );

  const invalidate = () => {
    queryClient.invalidateQueries(CATEGORY_LIMITS_QUERY_KEY);
    // Spend totals shown alongside limits come from the categories queries.
    queryClient.invalidateQueries("categories");
  };

  /**
   * Create or update a limit.
   *
   * The current-month payload only includes categories that already have a
   * limit, so the presence of an existing row is what decides POST vs PATCH.
   */
  const saveLimit = async ({
    categoryId,
    amount,
  }: {
    categoryId: number;
    amount: number;
  }) => {
    const existing = limitsByCategory.get(categoryId);
    const payloadAmount = amount.toFixed(2);

    if (existing) {
      const response = await CategoryLimitAPI.patch(existing.id, {
        amount: payloadAmount,
      });
      return response.data;
    }

    const response = await CategoryLimitAPI.post({
      category: categoryId,
      amount: payloadAmount,
    });
    return response.data;
  };

  const limitSaveMutation = useMutation({
    mutationFn: saveLimit,
    onError: (error) => {
      toast({
        title: "Couldn't save limit",
        description: extractLimitErrorMessage(
          error,
          "Something went wrong saving this limit. Please try again."
        ),
        variant: "destructive",
      });
    },
    onSuccess: () => {
      toast({
        title: "Limit saved",
        description: "Your monthly limit for this category has been updated.",
        variant: "default",
      });
      invalidate();
    },
  });

  /**
   * Clear a limit.
   *
   * There is no "no limit" amount on the backend — "no limit" means "no row",
   * so clearing is a DELETE of the limit row. Sending {amount: null} is a 400.
   */
  const clearLimit = async (limitId: number) => {
    const response = await CategoryLimitAPI.delete(limitId.toString());
    return response.data;
  };

  const limitClearMutation = useMutation({
    mutationFn: clearLimit,
    onError: (error) => {
      toast({
        title: "Couldn't remove limit",
        description: extractLimitErrorMessage(
          error,
          "Something went wrong removing this limit. Please try again."
        ),
        variant: "destructive",
      });
    },
    onSuccess: () => {
      toast({
        title: "Limit removed",
        description: "This category no longer has a monthly limit.",
        variant: "default",
      });
      invalidate();
    },
  });

  return {
    summary: summary ?? EMPTY_SUMMARY,
    limits: summary?.results ?? [],
    totals: summary?.totals ?? EMPTY_SUMMARY.totals,
    hasLimits: (summary?.results?.length ?? 0) > 0,
    limitsByCategory,
    getLimitForCategory,
    isLoading,
    isError,
    error,
    refetch,
    limitSaveMutation,
    limitClearMutation,
  };
};

export default useCategoryLimits;
